interface EffortRow {
  pos: number;
  label: string;
  detail?: string;
  pt: string;
  /** Short badge rendered next to the label, e.g. "Umsetzung". */
  tag?: string;
}

interface EffortBlock {
  name: string;
  /** One-line plain-language summary of what the block covers. */
  summary: string;
  subtotal: string;
  rows: EffortRow[];
}

interface EffortCard {
  label: string;
  value: string;
  sub: string;
  /** Renders the card on the dark brand surface — use for one card only. */
  featured?: boolean;
}

interface EffortLabels {
  block: string;
  effort: string;
  positions: string;
  pos: string;
  position: string;
  pt: string;
  details: string;
}

interface EffortTableProps {
  blocks: EffortBlock[];
  total: { label: string; value: string };
  labels: EffortLabels;
  cards: EffortCard[];
  scopeNote: string;
  note?: string;
  footnotes?: string[];
}

function positionRange(rows: EffortRow[]): string {
  const first = rows[0].pos;
  const last = rows[rows.length - 1].pos;
  return first === last ? `${first}` : `${first}–${last}`;
}

/**
 * EffortTable
 *
 * "Aufwand" section in three layers, most condensed first: a card band with
 * the headline figures, a block-level table (one row per work block, person
 * days right-aligned, total in the footer) and a collapsible panel with every
 * single line item. Readers who only want the number stop at the cards; the
 * full breakdown stays in the DOM for crawlers and answer engines.
 */
export default function EffortTable({
  blocks,
  total,
  labels,
  cards,
  scopeNote,
  note,
  footnotes,
}: EffortTableProps) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    throw new Error("EffortTable: `blocks` array is empty or invalid.");
  }
  if (!total?.label || !total?.value) {
    throw new Error("EffortTable: `total` is missing `label` or `value`.");
  }
  if (!labels?.block || !labels?.effort || !labels?.details) {
    throw new Error("EffortTable: `labels` is incomplete.");
  }
  if (!Array.isArray(cards) || cards.length === 0) {
    throw new Error("EffortTable: `cards` array is empty or invalid.");
  }
  for (const block of blocks) {
    if (!block.summary || !Array.isArray(block.rows) || block.rows.length === 0) {
      throw new Error(`EffortTable: block "${block.name}" needs \`summary\` and \`rows\`.`);
    }
  }

  return (
    <div className="effort">
      <div className="effort-cards">
        {cards.map((card) => (
          <div
            key={card.label}
            className={card.featured ? "effort-card effort-card-featured" : "effort-card"}
          >
            <p className="effort-card-label">{card.label}</p>
            <p className="effort-card-value">{card.value}</p>
            <p className="effort-card-sub">{card.sub}</p>
          </div>
        ))}
      </div>

      <div className="effort-table-wrap">
        <table className="effort-table">
          <caption className="effort-caption">{scopeNote}</caption>
          <thead>
            <tr>
              <th scope="col">{labels.block}</th>
              <th scope="col" className="effort-col-pt">{labels.effort}</th>
            </tr>
          </thead>
          <tbody>
            {blocks.map((block) => (
              <tr key={block.name}>
                <th scope="row" className="effort-block-cell">
                  <span className="effort-block-name">{block.name}</span>
                  <span className="effort-block-summary">{block.summary}</span>
                  <span className="effort-block-pos">
                    {labels.positions} {positionRange(block.rows)}
                  </span>
                </th>
                <td className="effort-col-pt">{block.subtotal}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">{total.label}</th>
              <td className="effort-col-pt">{total.value}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <details className="effort-details">
        <summary>{labels.details}</summary>
        <table className="effort-table effort-table-detail">
          <thead>
            <tr>
              <th scope="col" className="effort-col-pos">{labels.pos}</th>
              <th scope="col">{labels.position}</th>
              <th scope="col" className="effort-col-pt">{labels.pt}</th>
            </tr>
          </thead>
          {blocks.map((block) => (
            <tbody key={block.name}>
              <tr className="effort-group-row">
                <th colSpan={2} scope="rowgroup">{block.name}</th>
                <td className="effort-col-pt">{block.subtotal}</td>
              </tr>
              {block.rows.map((row) => (
                <tr key={row.pos}>
                  <td className="effort-col-pos">{row.pos}</td>
                  <td>
                    <span className="effort-row-label">{row.label}</span>
                    {row.tag ? <span className="effort-row-tag">{row.tag}</span> : null}
                    {row.detail ? (
                      <span className="effort-row-detail">{row.detail}</span>
                    ) : null}
                  </td>
                  <td className="effort-col-pt">{row.pt}</td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
        {footnotes?.length ? (
          <div className="effort-footnotes">
            {footnotes.map((footnote) => (
              <p key={footnote}>{footnote}</p>
            ))}
          </div>
        ) : null}
      </details>

      {note ? <p className="effort-note">{note}</p> : null}
    </div>
  );
}
